import { useState, useEffect, useMemo } from 'react'
import { PortalLayout } from '../components/PortalLayout'
import { api } from '../api'

// ═══════════════════════════════════════════════════════════════════════════════
// TYPE DEFINITIONS
// ═══════════════════════════════════════════════════════════════════════════════

const TABS = ['board', 'dispatch', 'live', 'analytics'] as const
type TabType = typeof TABS[number]
type ViewMode = 'day' | 'week'

interface Vehicle {
  id: number
  registration: string
  make: string
  model: string
  vehicle_type: string
  depot_id: number
  assigned_driver_id?: number | null
}

interface Driver {
  id: number
  name: string
  depot_id: number
  phone?: string
}

interface Job {
  id: number
  job_number: string
  status: 'awaiting_assignment' | 'booked_in' | 'assigned' | 'dispatched' | 'in_progress' | 'completed'
  job_date: string
  start_time: string
  customer_name: string
  customer_id: number
  collection_postcode: string
  delivery_postcode: string
  assigned_vehicle_id?: number | null
  assigned_driver_id?: number | null
  total_distance_miles?: number
  total_driving_hours?: number
  planned_start_time?: string
  planned_end_time?: string
  total_price?: number
}

interface OpsBoard {
  date_from: string
  date_to: string
  vehicles: Vehicle[]
  drivers: Driver[]
  jobs: Job[]
}

interface LiveJob {
  job_id: number
  job_number: string
  status: string
  customer_name: string
  driver_name: string
  vehicle_reg: string
  last_location?: {
    lat: number
    lng: number
    speed_kmh?: number
    recorded_at: string
  } | null
  isLate?: boolean
}

interface AnalyticsRow {
  job_id: number
  job_number: string
  job_date: string
  customer_name: string
  planned_distance_miles: number
  actual_distance_miles?: number
  planned_hours: number
  actual_hours?: number
  on_time: boolean
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export function OpsPage() {
  const [activeTab, setActiveTab] = useState<TabType>('board')
  const [viewMode, setViewMode] = useState<ViewMode>('day')
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0])

  // Board data
  const [board, setBoard] = useState<OpsBoard | null>(null)
  const [boardLoading, setBoardLoading] = useState(true)
  const [boardError, setBoardError] = useState<string | null>(null)

  // Live tracking
  const [liveJobs, setLiveJobs] = useState<LiveJob[]>([])
  const [liveLoading, setLiveLoading] = useState(false)
  const [lateAlerts, setLateAlerts] = useState<any[]>([])

  // Analytics
  const [analytics, setAnalytics] = useState<AnalyticsRow[]>([])
  const [analyticsDays, setAnalyticsDays] = useState(7)
  const [analyticsLoading, setAnalyticsLoading] = useState(false)

  // Filters
  const [filterDepot, setFilterDepot] = useState<number | null>(null)
  const [filterVehicleType, setFilterVehicleType] = useState<string>('')
  const [filterStatus, setFilterStatus] = useState<string>('')

  // Modal state
  const [showAllocateModal, setShowAllocateModal] = useState(false)
  const [selectedJobForAllocation, setSelectedJobForAllocation] = useState<Job | null>(null)
  const [allocateVehicleId, setAllocateVehicleId] = useState<number | null>(null)
  const [allocateDriverId, setAllocateDriverId] = useState<number | null>(null)
  const [allocatingJob, setAllocatingJob] = useState(false)

  // Job detail popover
  const [selectedJobForDetail, setSelectedJobForDetail] = useState<Job | null>(null)

  // Dispatch
  const [dispatchSelection, setDispatchSelection] = useState<Set<number>>(new Set())
  const [dispatchLoading, setDispatchLoading] = useState(false)

  // Load ops board
  useEffect(() => {
    const loadBoard = async () => {
      try {
        setBoardLoading(true)
        setBoardError(null)
        const dateFrom = viewMode === 'day' ? selectedDate : getWeekStart(selectedDate)
        const dateTo = viewMode === 'day' ? selectedDate : getWeekEnd(selectedDate)
        const data = await api.getOpsBoard({ date_from: dateFrom, date_to: dateTo })
        setBoard(data)
      } catch (err) {
        setBoardError(err instanceof Error ? err.message : 'Failed to load ops board')
      } finally {
        setBoardLoading(false)
      }
    }
    if (activeTab === 'board') {
      loadBoard()
    }
  }, [activeTab, selectedDate, viewMode])

  // Load live status
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>
    const loadLive = async () => {
      try {
        setLiveLoading(true)
        const [liveData, lateData] = await Promise.all([
          api.getLiveStatus(),
          api.getLateAlerts(),
        ])
        setLiveJobs(liveData || [])
        setLateAlerts(lateData || [])
      } catch (err) {
        console.error('Failed to load live data:', err)
      } finally {
        setLiveLoading(false)
      }
    }
    if (activeTab === 'live') {
      loadLive()
      interval = setInterval(loadLive, 30000)
    }
    return () => clearInterval(interval)
  }, [activeTab])

  // Load analytics
  useEffect(() => {
    const loadAnalytics = async () => {
      try {
        setAnalyticsLoading(true)
        const data = await api.getActualVsPlanned(analyticsDays)
        setAnalytics(data || [])
      } catch (err) {
        console.error('Failed to load analytics:', err)
      } finally {
        setAnalyticsLoading(false)
      }
    }
    if (activeTab === 'analytics') {
      loadAnalytics()
    }
  }, [activeTab, analyticsDays])

  // Handle allocation
  const handleAllocateClick = (job: Job) => {
    setSelectedJobForAllocation(job)
    setAllocateVehicleId(null)
    setAllocateDriverId(null)
    setShowAllocateModal(true)
  }

  const handleAllocateSubmit = async () => {
    if (!selectedJobForAllocation || !allocateVehicleId || !allocateDriverId) return
    try {
      setAllocatingJob(true)
      await api.allocateJob({
        job_id: selectedJobForAllocation.id,
        vehicle_id: allocateVehicleId,
        driver_id: allocateDriverId,
      })
      setShowAllocateModal(false)
      setSelectedJobForAllocation(null)
      // Reload board
      const dateFrom = viewMode === 'day' ? selectedDate : getWeekStart(selectedDate)
      const dateTo = viewMode === 'day' ? selectedDate : getWeekEnd(selectedDate)
      const data = await api.getOpsBoard({ date_from: dateFrom, date_to: dateTo })
      setBoard(data)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to allocate job')
    } finally {
      setAllocatingJob(false)
    }
  }

  // Handle dispatch
  const handleDispatchOne = async (jobId: number) => {
    try {
      await api.dispatchJob(jobId)
      // Reload dispatch tab
      const dateFrom = viewMode === 'day' ? selectedDate : getWeekStart(selectedDate)
      const dateTo = viewMode === 'day' ? selectedDate : getWeekEnd(selectedDate)
      const data = await api.getOpsBoard({ date_from: dateFrom, date_to: dateTo })
      setBoard(data)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to dispatch job')
    }
  }

  const handleDispatchSelected = async () => {
    if (dispatchSelection.size === 0) return
    try {
      setDispatchLoading(true)
      await Promise.all(
        Array.from(dispatchSelection).map(jobId => api.dispatchJob(jobId))
      )
      setDispatchSelection(new Set())
      // Reload
      const dateFrom = viewMode === 'day' ? selectedDate : getWeekStart(selectedDate)
      const dateTo = viewMode === 'day' ? selectedDate : getWeekEnd(selectedDate)
      const data = await api.getOpsBoard({ date_from: dateFrom, date_to: dateTo })
      setBoard(data)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to dispatch jobs')
    } finally {
      setDispatchLoading(false)
    }
  }

  // Filtered data
  const filteredVehicles = useMemo(() => {
    if (!board) return []
    return board.vehicles.filter(v => {
      if (filterDepot && v.depot_id !== filterDepot) return false
      if (filterVehicleType && v.vehicle_type !== filterVehicleType) return false
      return true
    })
  }, [board, filterDepot, filterVehicleType])

  const unallocatedJobs = useMemo(() => {
    if (!board) return []
    return board.jobs.filter(j => !j.assigned_vehicle_id)
  }, [board])

  const dispatchReadyJobs = useMemo(() => {
    if (!board) return []
    return board.jobs.filter(j => j.status === 'assigned')
  }, [board])

  const uniqueVehicleTypes = useMemo(() => {
    if (!board) return []
    return Array.from(new Set(board.vehicles.map(v => v.vehicle_type)))
  }, [board])

  const uniqueDepots = useMemo(() => {
    if (!board) return []
    return Array.from(new Set(board.vehicles.map(v => v.depot_id)))
  }, [board])

  // Render content based on active tab
  return (
    <PortalLayout currentPage="ops">
      <div className="min-h-screen bg-slate-50 p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-semibold text-slate-900 mb-2">OPERATIONS</h1>
          <p className="text-sm text-slate-600">Allocation Board, Dispatch Queue, Live Tracking & Analytics</p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6 border-b border-slate-200">
          <div className="flex gap-8">
            {TABS.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-1 py-3 font-semibold text-sm uppercase transition-colors ${
                  activeTab === tab
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {tab === 'board' && 'Allocation Board'}
                {tab === 'dispatch' && 'Dispatch Queue'}
                {tab === 'live' && 'Live Tracking'}
                {tab === 'analytics' && 'Analytics'}
              </button>
            ))}
          </div>
        </div>

        {/* TAB: ALLOCATION BOARD */}
        {activeTab === 'board' && (
          <div>
            {boardError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                {boardError}
              </div>
            )}

            {/* Board Controls */}
            <div className="mb-6 grid grid-cols-5 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase">View Mode</label>
                <select
                  value={viewMode}
                  onChange={e => setViewMode(e.target.value as ViewMode)}
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm bg-white"
                >
                  <option value="day">Day View</option>
                  <option value="week">Week View</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase">Date</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={e => setSelectedDate(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase">Depot</label>
                <select
                  value={filterDepot || ''}
                  onChange={e => setFilterDepot(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm bg-white"
                >
                  <option value="">All Depots</option>
                  {uniqueDepots.map(depot => (
                    <option key={depot} value={depot}>Depot {depot}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase">Vehicle Type</label>
                <select
                  value={filterVehicleType}
                  onChange={e => setFilterVehicleType(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm bg-white"
                >
                  <option value="">All Types</option>
                  {uniqueVehicleTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase">Status</label>
                <select
                  value={filterStatus}
                  onChange={e => setFilterStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm bg-white"
                >
                  <option value="">All Statuses</option>
                  <option value="unassigned">Unassigned</option>
                  <option value="assigned">Assigned</option>
                  <option value="dispatched">Dispatched</option>
                  <option value="in_progress">In Progress</option>
                </select>
              </div>
            </div>

            {/* Gantt Board */}
            {boardLoading ? (
              <div className="text-center py-12 text-slate-500">Loading board...</div>
            ) : (
              <div className="flex gap-6">
                {/* Main Board */}
                <div className="flex-1 bg-white border border-slate-200 rounded overflow-hidden">
                  {viewMode === 'day' ? (
                    <GanttDayView
                      vehicles={filteredVehicles}
                      jobs={board?.jobs || []}
                      drivers={board?.drivers || []}
                      selectedDate={selectedDate}
                      onJobClick={setSelectedJobForDetail}
                    />
                  ) : (
                    <GanttWeekView
                      vehicles={filteredVehicles}
                      jobs={board?.jobs || []}
                      drivers={board?.drivers || []}
                      startDate={getWeekStart(selectedDate)}
                      onJobClick={setSelectedJobForDetail}
                    />
                  )}
                </div>

                {/* Unallocated Jobs Sidebar */}
                <div className="w-80 bg-white border border-slate-200 rounded overflow-hidden flex flex-col">
                  <div className="bg-slate-900 text-white p-4">
                    <h3 className="font-semibold text-sm uppercase">Unallocated Jobs ({unallocatedJobs.length})</h3>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    {unallocatedJobs.length === 0 ? (
                      <div className="p-4 text-center text-slate-500 text-sm">No unallocated jobs</div>
                    ) : (
                      <div className="divide-y divide-slate-100">
                        {unallocatedJobs.map(job => (
                          <div key={job.id} className="p-4 hover:bg-slate-50 transition">
                            <div className="font-semibold text-xs text-slate-900 mb-1">{job.job_number}</div>
                            <div className="text-xs text-slate-600 mb-2">{job.customer_name}</div>
                            <div className="text-xs text-slate-500 mb-3">
                              <div>{job.collection_postcode} → {job.delivery_postcode}</div>
                              <div>{job.total_distance_miles?.toFixed(1) || '-'} mi • {job.total_driving_hours?.toFixed(1) || '-'} hrs</div>
                            </div>
                            <button
                              onClick={() => handleAllocateClick(job)}
                              className="w-full px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded hover:bg-blue-700 transition"
                            >
                              Allocate
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB: DISPATCH QUEUE */}
        {activeTab === 'dispatch' && (
          <div>
            <div className="mb-6 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">DISPATCH READY ({dispatchReadyJobs.length})</h2>
                <p className="text-sm text-slate-600 mt-1">Jobs assigned and ready for dispatch</p>
              </div>
              {dispatchSelection.size > 0 && (
                <button
                  onClick={handleDispatchSelected}
                  disabled={dispatchLoading}
                  className="px-6 py-2 bg-purple-600 text-white font-semibold text-sm uppercase rounded hover:bg-purple-700 disabled:opacity-50 transition"
                >
                  {dispatchLoading ? 'Dispatching...' : `Dispatch Selected (${dispatchSelection.size})`}
                </button>
              )}
            </div>

            {dispatchReadyJobs.length === 0 ? (
              <div className="p-12 text-center bg-slate-100 rounded border border-slate-200">
                <p className="text-slate-600 text-sm">No jobs ready for dispatch</p>
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={dispatchSelection.size === dispatchReadyJobs.length}
                          onChange={e => {
                            if (e.target.checked) {
                              setDispatchSelection(new Set(dispatchReadyJobs.map(j => j.id)))
                            } else {
                              setDispatchSelection(new Set())
                            }
                          }}
                          className="rounded"
                        />
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-900 uppercase text-xs">Job</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-900 uppercase text-xs">Customer</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-900 uppercase text-xs">Vehicle</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-900 uppercase text-xs">Driver</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-900 uppercase text-xs">Date / Time</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-900 uppercase text-xs">Route</th>
                      <th className="px-4 py-3 text-center font-semibold text-slate-900 uppercase text-xs">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {dispatchReadyJobs.map(job => {
                      const vehicle = board?.vehicles.find(v => v.id === job.assigned_vehicle_id)
                      const driver = board?.drivers.find(d => d.id === job.assigned_driver_id)
                      return (
                        <tr key={job.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={dispatchSelection.has(job.id)}
                              onChange={e => {
                                const newSet = new Set(dispatchSelection)
                                if (e.target.checked) {
                                  newSet.add(job.id)
                                } else {
                                  newSet.delete(job.id)
                                }
                                setDispatchSelection(newSet)
                              }}
                              className="rounded"
                            />
                          </td>
                          <td className="px-4 py-3 font-semibold text-slate-900">{job.job_number}</td>
                          <td className="px-4 py-3 text-slate-700">{job.customer_name}</td>
                          <td className="px-4 py-3 text-slate-700 text-xs">{vehicle?.registration || '-'}</td>
                          <td className="px-4 py-3 text-slate-700 text-xs">{driver?.name || '-'}</td>
                          <td className="px-4 py-3 text-slate-700 text-xs">{job.job_date} {job.start_time}</td>
                          <td className="px-4 py-3 text-slate-600 text-xs">{job.collection_postcode} → {job.delivery_postcode}</td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handleDispatchOne(job.id)}
                              className="px-3 py-1 bg-purple-600 text-white text-xs font-semibold rounded hover:bg-purple-700 transition"
                            >
                              Dispatch
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* TAB: LIVE TRACKING */}
        {activeTab === 'live' && (
          <div>
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">LIVE TRACKING</h2>

              {/* KPI Cards */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-white border border-slate-200 rounded p-4">
                  <div className="text-xs font-semibold text-slate-700 uppercase mb-1">Active Jobs</div>
                  <div className="text-3xl font-semibold text-slate-900">
                    {liveJobs.filter(j => j.status === 'in_progress').length}
                  </div>
                </div>
                <div className="bg-white border border-slate-200 rounded p-4">
                  <div className="text-xs font-semibold text-slate-700 uppercase mb-1">On Time %</div>
                  <div className="text-3xl font-semibold text-green-600">
                    {liveJobs.length > 0
                      ? Math.round((liveJobs.filter(j => !j.isLate).length / liveJobs.length) * 100)
                      : 0}%
                  </div>
                </div>
                <div className="bg-white border border-slate-200 rounded p-4">
                  <div className="text-xs font-semibold text-slate-700 uppercase mb-1">Late Count</div>
                  <div className="text-3xl font-semibold text-red-600">
                    {lateAlerts.length}
                  </div>
                </div>
              </div>
            </div>

            {liveLoading ? (
              <div className="text-center py-12 text-slate-500">Loading live data...</div>
            ) : liveJobs.length === 0 ? (
              <div className="p-12 text-center bg-slate-100 rounded border border-slate-200">
                <p className="text-slate-600 text-sm">No active jobs</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {liveJobs.map(job => (
                  <div
                    key={job.job_id}
                    className={`border rounded p-4 ${
                      job.isLate ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="font-semibold text-slate-900">{job.job_number}</div>
                        <div className="text-sm text-slate-600">{job.customer_name}</div>
                      </div>
                      <div
                        className={`px-2 py-1 rounded text-xs font-semibold ${
                          job.status === 'in_progress'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {job.status}
                      </div>
                    </div>
                    <div className="space-y-1 text-xs text-slate-600 mb-3">
                      <div>Driver: <span className="font-semibold text-slate-900">{job.driver_name}</span></div>
                      <div>Vehicle: <span className="font-semibold text-slate-900">{job.vehicle_reg}</span></div>
                      {job.last_location && (
                        <div>
                          Location: {job.last_location.lat.toFixed(4)}, {job.last_location.lng.toFixed(4)}
                          {job.last_location.speed_kmh && (
                            <span className="ml-2">@ {job.last_location.speed_kmh} km/h</span>
                          )}
                        </div>
                      )}
                    </div>
                    {job.isLate && (
                      <div className="text-xs font-semibold text-red-600 bg-red-100 rounded px-2 py-1">
                        LATE
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB: ANALYTICS */}
        {activeTab === 'analytics' && (
          <div>
            <div className="mb-6 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-slate-900">ACTUAL VS PLANNED</h2>
              <div className="flex gap-2">
                {[7, 14, 30].map(days => (
                  <button
                    key={days}
                    onClick={() => setAnalyticsDays(days)}
                    className={`px-4 py-2 rounded text-sm font-semibold transition ${
                      analyticsDays === days
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-200 text-slate-900 hover:bg-slate-300'
                    }`}
                  >
                    {days}d
                  </button>
                ))}
              </div>
            </div>

            {analyticsLoading ? (
              <div className="text-center py-12 text-slate-500">Loading analytics...</div>
            ) : analytics.length === 0 ? (
              <div className="p-12 text-center bg-slate-100 rounded border border-slate-200">
                <p className="text-slate-600 text-sm">No analytics data available</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* KPI Cards */}
                <div className="grid grid-cols-3 gap-4">
                  {(() => {
                    const avgDistanceVar = analytics.length > 0
                      ? (analytics.reduce((sum, row) => sum + ((row.actual_distance_miles || row.planned_distance_miles) - row.planned_distance_miles), 0) / analytics.length)
                      : 0
                    const avgHoursVar = analytics.length > 0
                      ? (analytics.reduce((sum, row) => sum + ((row.actual_hours || row.planned_hours) - row.planned_hours), 0) / analytics.length)
                      : 0
                    const onTimeCount = analytics.filter(r => r.on_time).length
                    const onTimePercent = analytics.length > 0 ? Math.round((onTimeCount / analytics.length) * 100) : 0

                    return (
                      <>
                        <div className="bg-white border border-slate-200 rounded p-4">
                          <div className="text-xs font-semibold text-slate-700 uppercase mb-1">Avg Distance Variance</div>
                          <div className={`text-2xl font-semibold ${avgDistanceVar > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {avgDistanceVar > 0 ? '+' : ''}{avgDistanceVar.toFixed(1)} mi
                          </div>
                        </div>
                        <div className="bg-white border border-slate-200 rounded p-4">
                          <div className="text-xs font-semibold text-slate-700 uppercase mb-1">Avg Hours Variance</div>
                          <div className={`text-2xl font-semibold ${avgHoursVar > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {avgHoursVar > 0 ? '+' : ''}{avgHoursVar.toFixed(2)} hrs
                          </div>
                        </div>
                        <div className="bg-white border border-slate-200 rounded p-4">
                          <div className="text-xs font-semibold text-slate-700 uppercase mb-1">On Time %</div>
                          <div className="text-2xl font-semibold text-blue-600">{onTimePercent}%</div>
                        </div>
                      </>
                    )
                  })()}
                </div>

                {/* Detailed Table */}
                <div className="bg-white border border-slate-200 rounded overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-slate-900 uppercase text-xs">Job</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-900 uppercase text-xs">Customer</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-900 uppercase text-xs">Date</th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-900 uppercase text-xs">Planned Dist</th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-900 uppercase text-xs">Actual Dist</th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-900 uppercase text-xs">Var</th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-900 uppercase text-xs">Planned Hrs</th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-900 uppercase text-xs">Actual Hrs</th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-900 uppercase text-xs">Var</th>
                        <th className="px-4 py-3 text-center font-semibold text-slate-900 uppercase text-xs">On Time</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {analytics.map(row => {
                        const distVar = (row.actual_distance_miles || row.planned_distance_miles) - row.planned_distance_miles
                        const hoursVar = (row.actual_hours || row.planned_hours) - row.planned_hours
                        return (
                          <tr key={row.job_id} className="hover:bg-slate-50">
                            <td className="px-4 py-3 font-semibold text-slate-900">{row.job_number}</td>
                            <td className="px-4 py-3 text-slate-700">{row.customer_name}</td>
                            <td className="px-4 py-3 text-slate-600 text-xs">{row.job_date}</td>
                            <td className="px-4 py-3 text-right text-slate-700">{row.planned_distance_miles.toFixed(1)}</td>
                            <td className="px-4 py-3 text-right text-slate-700">{(row.actual_distance_miles || '-')}</td>
                            <td className={`px-4 py-3 text-right font-semibold ${distVar > 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {distVar > 0 ? '+' : ''}{distVar.toFixed(1)}
                            </td>
                            <td className="px-4 py-3 text-right text-slate-700">{row.planned_hours.toFixed(2)}</td>
                            <td className="px-4 py-3 text-right text-slate-700">{(row.actual_hours || '-')}</td>
                            <td className={`px-4 py-3 text-right font-semibold ${hoursVar > 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {hoursVar > 0 ? '+' : ''}{hoursVar.toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span
                                className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                                  row.on_time ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {row.on_time ? 'Yes' : 'No'}
                              </span>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ALLOCATION MODAL */}
        {showAllocateModal && selectedJobForAllocation && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded shadow-lg w-96 max-h-[90vh] overflow-y-auto">
              <div className="bg-slate-900 text-white p-4 sticky top-0">
                <h3 className="font-semibold text-lg">Allocate Job {selectedJobForAllocation.job_number}</h3>
                <p className="text-sm text-slate-300 mt-1">{selectedJobForAllocation.customer_name}</p>
              </div>

              <div className="p-6 space-y-6">
                {/* Job Details */}
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">JOB DETAILS</label>
                  <div className="space-y-1 text-sm text-slate-600">
                    <div>{selectedJobForAllocation.job_date} {selectedJobForAllocation.start_time}</div>
                    <div>{selectedJobForAllocation.collection_postcode} → {selectedJobForAllocation.delivery_postcode}</div>
                    <div>{selectedJobForAllocation.total_distance_miles?.toFixed(1) || '-'} miles • {selectedJobForAllocation.total_driving_hours?.toFixed(1) || '-'} hours</div>
                  </div>
                </div>

                {/* Vehicle Selection */}
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-3 uppercase">Select Vehicle</label>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {(board?.vehicles || []).map(vehicle => (
                      <button
                        key={vehicle.id}
                        onClick={() => setAllocateVehicleId(vehicle.id)}
                        className={`w-full text-left px-4 py-3 rounded border-2 transition ${
                          allocateVehicleId === vehicle.id
                            ? 'border-blue-600 bg-blue-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className="font-semibold text-slate-900">{vehicle.registration}</div>
                        <div className="text-xs text-slate-600">{vehicle.make} {vehicle.model} ({vehicle.vehicle_type})</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Driver Selection */}
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-3 uppercase">Select Driver</label>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {(board?.drivers || []).map(driver => (
                      <button
                        key={driver.id}
                        onClick={() => setAllocateDriverId(driver.id)}
                        className={`w-full text-left px-4 py-3 rounded border-2 transition ${
                          allocateDriverId === driver.id
                            ? 'border-blue-600 bg-blue-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className="font-semibold text-slate-900">{driver.name}</div>
                        <div className="text-xs text-slate-600">{driver.phone || 'No phone'}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t border-slate-200">
                  <button
                    onClick={() => setShowAllocateModal(false)}
                    className="flex-1 px-4 py-2 border border-slate-300 text-slate-900 font-semibold rounded hover:bg-slate-50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAllocateSubmit}
                    disabled={!allocateVehicleId || !allocateDriverId || allocatingJob}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white font-semibold rounded hover:bg-blue-700 disabled:opacity-50 transition"
                  >
                    {allocatingJob ? 'Allocating...' : 'Allocate'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </PortalLayout>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// GANTT DAY VIEW COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

function GanttDayView({
  vehicles,
  jobs,
  drivers,
  selectedDate,
  onJobClick,
}: {
  vehicles: Vehicle[]
  jobs: Job[]
  drivers: Driver[]
  selectedDate: string
  onJobClick: (job: Job) => void
}) {
  const hours = Array.from({ length: 17 }, (_, i) => i + 6) // 06:00 to 22:00
  const dayJobs = jobs.filter(j => j.job_date === selectedDate)

  const timeToPercent = (timeStr: string): number => {
    const [h, m] = timeStr.split(':').map(Number)
    const mins = h * 60 + m
    const startMins = 6 * 60
    return ((mins - startMins) / (16 * 60)) * 100
  }

  const getJobColor = (status: string) => {
    switch (status) {
      case 'awaiting_assignment':
      case 'booked_in':
        return 'bg-amber-500'
      case 'assigned':
        return 'bg-blue-500'
      case 'dispatched':
        return 'bg-purple-500'
      case 'in_progress':
        return 'bg-green-500'
      case 'completed':
        return 'bg-slate-500'
      default:
        return 'bg-slate-400'
    }
  }

  return (
    <div className="overflow-x-auto">
      <div className="inline-block min-w-full">
        {/* Header with hours */}
        <div className="flex">
          <div className="w-64 flex-shrink-0 border-r border-slate-200 bg-slate-50"></div>
          <div className="flex-1 flex">
            {hours.map(hour => (
              <div
                key={hour}
                className="flex-1 border-r border-slate-100 text-center px-2 py-2 text-xs font-semibold text-slate-700 bg-slate-50"
              >
                {String(hour).padStart(2, '0')}:00
              </div>
            ))}
          </div>
        </div>

        {/* Vehicle rows */}
        {vehicles.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">No vehicles match the selected filters</div>
        ) : (
          vehicles.map(vehicle => {
            const vehicleJobs = dayJobs.filter(j => j.assigned_vehicle_id === vehicle.id)
            const driver = drivers.find(d => d.id === vehicle.assigned_driver_id)
            return (
              <div key={vehicle.id} className="flex border-b border-slate-200 hover:bg-slate-50 transition">
                {/* Vehicle info */}
                <div className="w-64 flex-shrink-0 border-r border-slate-200 p-3 bg-slate-50">
                  <div className="font-semibold text-sm text-slate-900">{vehicle.registration}</div>
                  <div className="text-xs text-slate-600">{vehicle.make} {vehicle.model}</div>
                  <div className="text-xs text-slate-500 mt-1">{vehicle.vehicle_type}</div>
                  {driver && <div className="text-xs font-semibold text-blue-600 mt-1">{driver.name}</div>}
                </div>

                {/* Timeline */}
                <div className="flex-1 relative h-24 bg-white">
                  {hours.map((_, i) => (
                    <div
                      key={i}
                      className="absolute top-0 bottom-0 border-r border-slate-100"
                      style={{ left: `${(i / hours.length) * 100}%` }}
                    ></div>
                  ))}

                  {/* Job blocks */}
                  {vehicleJobs.map(job => {
                    const startPercent = timeToPercent(job.planned_start_time || job.start_time)
                    const endPercent = timeToPercent(job.planned_end_time || '22:00')
                    return (
                      <div
                        key={job.id}
                        onClick={() => onJobClick(job)}
                        className={`absolute top-2 bottom-2 ${getJobColor(job.status)} text-white text-xs cursor-pointer rounded overflow-hidden hover:opacity-90 transition flex flex-col justify-center px-2`}
                        style={{
                          left: `${startPercent}%`,
                          right: `${100 - endPercent}%`,
                        }}
                      >
                        <div className="font-semibold truncate">{job.job_number}</div>
                        <div className="truncate text-xs opacity-90">{job.customer_name}</div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// GANTT WEEK VIEW COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

function GanttWeekView({
  vehicles,
  jobs,
  drivers,
  startDate,
  onJobClick,
}: {
  vehicles: Vehicle[]
  jobs: Job[]
  drivers: Driver[]
  startDate: string
  onJobClick: (job: Job) => void
}) {
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(startDate)
    d.setDate(d.getDate() + i)
    return d.toISOString().split('T')[0]
  })

  const dayNames = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']

  const getJobColor = (status: string) => {
    switch (status) {
      case 'awaiting_assignment':
      case 'booked_in':
        return 'bg-amber-500'
      case 'assigned':
        return 'bg-blue-500'
      case 'dispatched':
        return 'bg-purple-500'
      case 'in_progress':
        return 'bg-green-500'
      case 'completed':
        return 'bg-slate-500'
      default:
        return 'bg-slate-400'
    }
  }

  return (
    <div className="overflow-x-auto">
      <div className="inline-block min-w-full">
        {/* Header */}
        <div className="flex">
          <div className="w-64 flex-shrink-0 border-r border-slate-200 bg-slate-50"></div>
          <div className="flex">
            {days.map((day, i) => (
              <div
                key={day}
                className="w-48 border-r border-slate-200 px-4 py-3 text-center bg-slate-50"
              >
                <div className="font-semibold text-sm text-slate-900">{dayNames[i]}</div>
                <div className="text-xs text-slate-600">{day}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Vehicle rows */}
        {vehicles.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">No vehicles match the selected filters</div>
        ) : (
          vehicles.map(vehicle => {
            const driver = drivers.find(d => d.id === vehicle.assigned_driver_id)
            return (
              <div key={vehicle.id} className="flex border-b border-slate-200 hover:bg-slate-50 transition">
                {/* Vehicle info */}
                <div className="w-64 flex-shrink-0 border-r border-slate-200 p-3 bg-slate-50">
                  <div className="font-semibold text-sm text-slate-900">{vehicle.registration}</div>
                  <div className="text-xs text-slate-600">{vehicle.make} {vehicle.model}</div>
                  <div className="text-xs text-slate-500 mt-1">{vehicle.vehicle_type}</div>
                  {driver && <div className="text-xs font-semibold text-blue-600 mt-1">{driver.name}</div>}
                </div>

                {/* Day columns */}
                <div className="flex">
                  {days.map(day => {
                    const dayJobs = jobs.filter(j => j.job_date === day && j.assigned_vehicle_id === vehicle.id)
                    return (
                      <div key={day} className="w-48 border-r border-slate-200 p-2 min-h-24 space-y-1">
                        {dayJobs.map(job => (
                          <div
                            key={job.id}
                            onClick={() => onJobClick(job)}
                            className={`${getJobColor(job.status)} text-white text-xs p-2 rounded cursor-pointer hover:opacity-90 transition`}
                          >
                            <div className="font-semibold truncate">{job.job_number}</div>
                            <div className="truncate">{job.customer_name}</div>
                            <div className="text-xs opacity-90">{job.start_time}</div>
                          </div>
                        ))}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

function getWeekStart(dateStr: string): string {
  const date = new Date(dateStr)
  const day = date.getDay() || 7
  const diff = date.getDate() - day + 1
  const weekStart = new Date(date.setDate(diff))
  return weekStart.toISOString().split('T')[0]
}

function getWeekEnd(dateStr: string): string {
  const start = getWeekStart(dateStr)
  const end = new Date(start)
  end.setDate(end.getDate() + 6)
  return end.toISOString().split('T')[0]
}
